/**
 * `marketplace.report` — reporting an agent (PRD-18 AGM-123).
 *
 * The categories are written as sentences a person would actually say about something that
 * went wrong, not as moderation taxonomy — "It gets things wrong" is a thing anyone can
 * pick correctly, "inaccuracy" is a thing people skip. The values behind them are the
 * contract's enum, so the queue an administrator reads is still typed.
 *
 * The reporter is told exactly who sees their name, because the alternative is that they
 * either over-report (believing it anonymous) or under-report (fearing the owner will know).
 *
 * ── Why this namespace exists beside `admin.reports` ──
 *
 * The same five enum values are worded twice, on purpose (PRD-27 D-9). Here they are
 * first-person sentences for somebody reporting an agent; over there they are a moderation
 * taxonomy for the administrator triaging it. A translator handed one shared map has no way
 * to know that and will "fix" the inconsistency, which is precisely why the two namespaces
 * are kept apart rather than folded into a shared `enums` file.
 */

import type { AgentReport } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { I18n } from '@/i18n/I18nProvider.tsx'

/** The contract's own enum, so a sixth category cannot be added without a word for it. */
type ReportCategory = AgentReport['category']

export const report = {
  open: 'Report this agent',
  title: (agentName: string): string => `Report ${agentName}`,
  lead: 'This goes to an administrator. The agent’s owner never learns who reported it.',
  categoryLabel: 'What is wrong',
  /**
   * `satisfies` rather than a bare literal: this is the second set of words for the same
   * contract enum (the triage queue has the administrator's), and an unlabelled category
   * would render as nothing at all in a radio list. Typed, a sixth member is a compile
   * error here rather than a blank row somebody reports as a rendering glitch (D-9).
   */
  categories: {
    impersonation: 'It pretends to be official, or to be someone it is not',
    harmful: 'It said something harmful or unsafe',
    wrong: 'It gets things wrong',
    inappropriate: 'It is not appropriate for work',
    other: 'Something else'
  } satisfies Record<ReportCategory, string>,
  noteLabel: 'Anything else an administrator should know — optional',
  notePlaceholder: 'What happened, and what you expected instead.',
  /**
   * Said "1 character left" at one — an English plural bug that was in the product (F-22),
   * fixed here rather than carried into a second language. `pluralCategory` rather than
   * `left === 1`, because Brazilian Portuguese puts zero in the singular category, so the
   * comparison would be wrong on the exhausted counter in one of the two shipped languages.
   */
  noteRemaining: (fmt: Pick<I18n, 'locale' | 'formatCount'>, left: number): string =>
    pluralCategory(fmt.locale, left) === 'one'
      ? `${fmt.formatCount(left)} character left`
      : `${fmt.formatCount(left)} characters left`,
  submit: 'Send report',
  submitting: 'Sending…',
  cancel: 'Cancel',
  close: 'Close',
  sent: 'Reported — an administrator will review.',
  duplicate: 'You already reported this agent. An administrator still has it open.',
  failed: 'The report could not be sent. Try again in a moment.'
}
